import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/src/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface PortalAuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      // On sign-in, record the member's login (also activates a pending invite).
      if (event === "SIGNED_IN" && session?.user) {
        const u = session.user;
        setTimeout(() => {
          supabase.rpc("record_member_login").then(({ error }) => {
            if (error) console.debug("record_member_login:", error.message);
          });
          import("@/lib/portal/activityLog").then(({ logPartnerActivity }) => {
            const meta = (u.user_metadata ?? {}) as Record<string, string>;
            const name =
              `${meta.first_name ?? ""} ${meta.last_name ?? ""}`.trim() || u.email || null;
            logPartnerActivity({ userId: u.id, action: "login", actorName: name });
          });
        }, 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? new Error(error.message) : null };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error: error ? new Error(error.message) : null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <PortalAuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used within PortalAuthProvider");
  return ctx;
}
